/**
 * Nombres de item DERIVADOS de sus afijos rolled (estilo Diablo 2 / PoE "mágico",
 * WoW "of the X"). El nombre es una FUNCIÓN PURA de los afijos + su tier: mismos
 * rolls → mismo nombre, y el nombre comunica de un vistazo qué hace el item.
 *
 * - Se computa AL VUELO desde `RolledAffixes` (sin campo nuevo en BD, sin migración).
 * - Un afijo aporta un PREFIJO (adjetivo, concuerda en género/número con el item) y/u
 *   otro aporta un SUFIJO ("de X", invariante).
 * - Banco data-driven `AFFIX_NAMING`, keyeado con la MISMA clave que aparece en
 *   `RolledAffixes` (= `affixRolledKey`: `modifierKey` plano para afijos core, `id`
 *   para los generados por Reino / tipo de arma). Varias opciones por tier → variedad.
 *
 * Invariante del banco (lo verifica `assertNamingCoverage`): toda familia con
 * `prefixes` DEBE tener también `suffixes` (así el reparto prefijo/sufijo de un EPIC
 * nunca pierde el flavor de un afijo). Las familias de utilidad/nicho van suffix-only.
 *
 * Ver docs/ITEM_NAMING.md.
 */

import { AFFIX_POOL, affixRolledKey } from './affixes';
import { getKingdom } from './kingdoms';
import type { RolledAffixes } from '../types/item';
import { WeaponSubtype } from '../types/item';

/** Palabras por tier: `[T1, T2, T3]`, cada tier con 1+ opciones. */
export type TierWords = [readonly string[], readonly string[], readonly string[]];

export interface AffixNaming {
  /** Adjetivos en MASCULINO SINGULAR (se concuerdan al render con `agreeAdjective`). */
  prefixes?: TierWords;
  /** Sufijos "de X" / "del X" — INVARIANTES, no concuerdan. */
  suffixes?: TierWords;
}

/** Género + número gramatical del item base (para concordar los prefijos adjetivos). */
export interface ItemGrammar {
  gender: 'm' | 'f';
  plural?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Banco CORE (afijos de modifier key plano). Familias P = prefijo + sufijo;
// familias S = sólo sufijo (utility/nicho donde un adjetivo queda forzado).
// ─────────────────────────────────────────────────────────────────────────────
const CORE_NAMING: Record<string, AffixNaming> = {
  // ===== Recursos =====
  hp: {
    prefixes: [
      ['Robusto', 'Sano', 'Fornido', 'Recio'],
      ['Vigoroso', 'Hercúleo', 'Corpulento', 'Bravío'],
      ['Titánico', 'Colosal', 'Monolítico', 'Descomunal'],
    ],
    suffixes: [['del Buey', 'del Jabalí'], ['del Oso', 'del Búfalo'], ['del Coloso', 'del Behemot']],
  },
  mana: {
    prefixes: [
      ['Mágico', 'Iniciado', 'Rúnico'],
      ['Arcano', 'Encantado', 'Hechizado'],
      ['Etéreo', 'Sublime', 'Trascendente'],
    ],
    suffixes: [['del Aprendiz', 'del Adepto'], ['del Mago', 'del Hechicero'], ['del Archimago', 'del Cónclave']],
  },
  stamina: {
    prefixes: [
      ['Enérgico', 'Activo', 'Brioso'],
      ['Incansable', 'Tenaz', 'Fibroso'],
      ['Inagotable', 'Infatigable', 'Indómito'],
    ],
    suffixes: [['del Corredor', 'del Nómada'], ['del Maratón', 'del Fondista'], ['del Titán', 'del Coloso']],
  },

  // ===== Defensas =====
  physDef: {
    prefixes: [
      ['Reforzado', 'Firme', 'Recubierto'],
      ['Blindado', 'Fortificado', 'Acorazado'],
      ['Impenetrable', 'Inexpugnable', 'Inviolable'],
    ],
    suffixes: [['del Guardián', 'del Centinela'], ['del Baluarte', 'del Muro'], ['de la Fortaleza', 'del Bastión']],
  },
  magicDef: {
    prefixes: [
      ['Protegido', 'Rúnico', 'Bendito'],
      ['Consagrado', 'Abjurado', 'Purificado'],
      ['Sagrado', 'Inviolable', 'Celestial'],
    ],
    suffixes: [['del Acólito', 'del Iniciado'], ['del Sacerdote', 'del Templario'], ['del Arcángel', 'del Santo']],
  },

  // ===== Daño (armas) =====
  minDamage: {
    prefixes: [
      ['Afilado', 'Puntiagudo', 'Aguzado'],
      ['Cortante', 'Penetrante', 'Incisivo'],
      ['Devastador', 'Despiadado', 'Lacerante'],
    ],
    suffixes: [['del Filo', 'del Corte'], ['del Verdugo', 'del Desollador'], ['de la Masacre', 'de la Carnicería']],
  },
  maxDamage: {
    prefixes: [
      ['Pesado', 'Contundente', 'Macizo'],
      ['Brutal', 'Feroz', 'Demoledor'],
      ['Aniquilador', 'Cataclísmico', 'Apocalíptico'],
    ],
    suffixes: [['del Golpe', 'del Impacto'], ['del Estrago', 'de la Ruina'], ['del Cataclismo', 'del Apocalipsis']],
  },
  damagePercent: {
    prefixes: [
      ['Salvaje', 'Bravo', 'Fiero'],
      ['Cruel', 'Bárbaro', 'Sañudo'],
      ['Implacable', 'Despiadado', 'Inclemente'],
    ],
    suffixes: [['de la Ira', 'del Ímpetu'], ['de la Furia', 'de la Saña'], ['de la Aniquilación', 'del Exterminio']],
  },

  // ===== Velocidades =====
  moveSpeed: {
    prefixes: [
      ['Ágil', 'Ligero', 'Liviano'],
      ['Veloz', 'Raudo', 'Presto'],
      ['Fugaz', 'Vertiginoso', 'Alado'],
    ],
    suffixes: [['de la Brisa', 'de la Liebre'], ['del Viento', 'del Corcel'], ['del Vendaval', 'del Cometa']],
  },
  attackSpeed: {
    prefixes: [
      ['Rápido', 'Diestro', 'Suelto'],
      ['Frenético', 'Presto', 'Vertiginoso'],
      ['Relampagueante', 'Ciclónico', 'Fulgurante'],
    ],
    suffixes: [['del Duelista', 'del Espadachín'], ['del Frenesí', 'del Berserker'], ['del Huracán', 'del Torbellino']],
  },
  castSpeed: {
    prefixes: [
      ['Fluido', 'Suelto', 'Diestro'],
      ['Célere', 'Presto', 'Raudo'],
      ['Instantáneo', 'Fulminante', 'Relampagueante'],
    ],
    suffixes: [['del Conjurador', 'del Adepto'], ['del Hechicero', 'del Taumaturgo'], ['del Cónclave', 'del Archimago']],
  },
  spellRangeBonus: {
    suffixes: [['del Vidente', 'del Ojo Lejano'], ['del Oráculo', 'del Otero'], ['del Horizonte', 'del Confín']],
  },
  summonLimit: {
    suffixes: [['del Invocador', 'del Convocador'], ['del Nigromante', 'del Brujo'], ['del Amo de Bestias', 'del Señor de Legiones']],
  },

  // ===== Críticos =====
  critChance: {
    prefixes: [
      ['Certero', 'Preciso', 'Agudo'],
      ['Letal', 'Mortífero', 'Ponzoñoso'],
      ['Asesino', 'Ejecutor', 'Implacable'],
    ],
    suffixes: [['del Cazador', 'del Ojeador'], ['del Asesino', 'del Sicario'], ['del Verdugo', 'del Ejecutor']],
  },
  critMultiplier: {
    prefixes: [
      ['Cruento', 'Severo', 'Áspero'],
      ['Salvaje', 'Sañudo', 'Brutal'],
      ['Catastrófico', 'Aniquilador', 'Devastador'],
    ],
    suffixes: [['del Carnicero', 'del Matarife'], ['del Golpe Mortal', 'del Tajo Cruel'], ['del Aniquilador', 'del Exterminio']],
  },

  // ===== Evasión =====
  evasion: {
    prefixes: [
      ['Escurridizo', 'Evasivo', 'Resbaladizo'],
      ['Furtivo', 'Elusivo', 'Huidizo'],
      ['Etéreo', 'Fantasmal', 'Intangible'],
    ],
    suffixes: [['del Viento', 'de la Sombra'], ['del Espectro', 'del Felino'], ['del Fantasma', 'del Espejismo']],
  },

  // ===== Regeneración =====
  regenHP: { suffixes: [['del Regenerado', 'del Recio'], ['del Trol', 'del Sanador'], ['del Fénix', 'de la Hidra']] },
  regenMana: { suffixes: [['del Manantial', 'del Arroyo'], ['del Sabio', 'del Erudito'], ['del Plano Astral', 'del Maná Eterno']] },
  regenStamina: { suffixes: [['del Descanso', 'del Respiro'], ['del Segundo Aliento', 'del Sereno'], ['del Incansable', 'del Infatigable']] },

  // ===== Porcentuales =====
  hpPercent: {
    prefixes: [
      ['Sano', 'Pleno', 'Entero'],
      ['Vital', 'Pletórico', 'Rebosante'],
      ['Indestructible', 'Perenne', 'Imperecedero'],
    ],
    suffixes: [['de la Vitalidad', 'del Vigor'], ['del Longevo', 'del Resistente'], ['del Inmortal', 'del Imperecedero']],
  },
  manaPercent: { suffixes: [['del Erudito', 'del Estudioso'], ['del Sabio Arcano', 'del Vidente'], ['del Plano Astral', 'del Océano Arcano']] },
  staminaPercent: { suffixes: [['del Aliento', 'del Brío'], ['del Fondista', 'del Nómada'], ['del Coloso', 'del Inagotable']] },
  attackSpeedPercent: { suffixes: [['del Vértigo', 'del Repique'], ['del Torbellino', 'del Remolino'], ['del Ciclón', 'de la Tempestad']] },
  moveSpeedPercent: { suffixes: [['del Trotamundos', 'del Andarín'], ['del Corcel', 'del Galope'], ['del Cometa', 'del Meteoro']] },
  critChancePercent: { suffixes: [['del Ojo Certero', 'del Tino'], ['del Francotirador', 'del Puntería'], ['del Ojo de Halcón', 'del Águila']] },
  critMultiplierPercent: { suffixes: [['del Impacto', 'del Golpe'], ['del Devastador', 'del Estrago'], ['del Cataclismo', 'del Apocalipsis']] },

  // ===== Utility =====
  magicFind: { suffixes: [['del Buscador', 'del Rastreador'], ['del Saqueador', 'del Codicioso'], ['del Rey Midas', 'del Avaro']] },
  healingReceived: { suffixes: [['del Convaleciente', 'del Cuidado'], ['del Sanado', 'del Restaurado'], ['del Bendecido', 'del Milagro']] },

  // ===== Reactivos de combate =====
  lifeStealPhysical: {
    prefixes: [
      ['Sediento', 'Ávido', 'Hambriento'],
      ['Sanguinario', 'Cruento', 'Sangriento'],
      ['Vampírico', 'Carmesí', 'Hemófago'],
    ],
    suffixes: [['de la Sanguijuela', 'del Chupasangre'], ['del Vástago', 'del Colmillo'], ['del Rey Carmesí', 'del No-Muerto Sediento']],
  },
  lifeStealMagic: {
    prefixes: [
      ['Drenante', 'Absorbente', 'Chupador'],
      ['Devorador', 'Voraz', 'Consumidor'],
      ['Insaciable', 'Abisal', 'Famélico'],
    ],
    suffixes: [['del Sifón', 'de la Sanguijuela Arcana'], ['del Devorador', 'del Parásito'], ['del Abismo', 'del Vacío Hambriento']],
  },
  reflectPhysicalDamage: {
    prefixes: [
      ['Espinoso', 'Áspero', 'Erizado'],
      ['Punzante', 'Hiriente', 'Pinchudo'],
      ['Vengativo', 'Inclemente', 'Retributivo'],
    ],
    suffixes: [['de las Espinas', 'del Cardo'], ['del Erizo', 'del Puercoespín'], ['de la Venganza', 'del Escarmiento']],
  },
  reflectMagicDamage: { suffixes: [['del Reflejo', 'del Eco'], ['del Prisma', 'de la Barrera'], ['del Espejo Arcano', 'del Contrahechizo']] },

  // ===== Pérdida de movilidad (procs / DR) =====
  immobilizeOnHitPhysical: { suffixes: [['de la Raíz', 'de la Liana'], ['del Cepo', 'de la Red'], ['de las Cadenas', 'del Grillete']] },
  immobilizeOnHitSpell: { suffixes: [['de la Escarcha', 'del Rocío Helado'], ['del Hielo', 'de la Nieve'], ['del Glaciar', 'del Invierno Eterno']] },
  paralyzeOnHitPhysical: { suffixes: [['del Aturdidor', 'del Porrazo'], ['del Mazazo', 'del Coscorrón'], ['del Trueno', 'del Cataclismo']] },
  paralyzeOnHitSpell: { suffixes: [['del Shock', 'de la Chispa'], ['del Rayo', 'de la Descarga'], ['de la Tormenta', 'del Trueno Divino']] },
  immobilizeDurationReduction: { suffixes: [['del Libre', 'del Suelto'], ['del Indómito', 'del Escurridizo'], ['del Imparable', 'del Desatado']] },
  paralyzeDurationReduction: { suffixes: [['del Firme', 'del Estoico'], ['del Inquebrantable', 'del Inmutable'], ['del Sereno', 'del Imperturbable']] },
};

/** Labels plural de subtipos físicos (para los sufijos "del Maestro de X"). */
const WEAPON_SUBTYPE_PLURAL: Partial<Record<WeaponSubtype, string>> = {
  [WeaponSubtype.SWORD]: 'Espadas',
  [WeaponSubtype.AXE]: 'Hachas',
  [WeaponSubtype.MACE]: 'Mazas',
  [WeaponSubtype.DAGGER]: 'Dagas',
  [WeaponSubtype.KNUCKLES]: 'Nudilleras',
  [WeaponSubtype.BOW]: 'Arcos',
  [WeaponSubtype.CROSSBOW]: 'Ballestas',
  [WeaponSubtype.THROWABLE]: 'Arrojadizas',
};

/**
 * Banco completo. Arranca del CORE y suma dinámicamente las familias generadas por
 * Reino y por tipo de arma, leyéndolas del PROPIO `AFFIX_POOL` para que la clave
 * coincida EXACTO con la que produce `rollAffixes` (garantiza cobertura sin huecos).
 */
export const AFFIX_NAMING: Record<string, AffixNaming> = (() => {
  const bank: Record<string, AffixNaming> = { ...CORE_NAMING };
  for (const aff of AFFIX_POOL) {
    if (aff.kingdom) {
      const label = getKingdom(aff.kingdom)?.labelEsPlural ?? aff.kingdom;
      if (aff.id.startsWith('aff_dmg_vs_')) {
        bank[aff.id] = {
          suffixes: [
            [`del Cazador de ${label}`, `del Ojeador de ${label}`],
            [`del Verdugo de ${label}`, `del Matador de ${label}`],
            [`del Azote de ${label}`, `del Exterminador de ${label}`],
          ],
        };
      } else if (aff.id.startsWith('aff_mf_vs_')) {
        bank[aff.id] = {
          suffixes: [
            [`del Rastreador de ${label}`, `del Buscador de ${label}`],
            [`del Saqueador de ${label}`, `del Codicioso de ${label}`],
            [`del Expoliador de ${label}`, `del Profanador de ${label}`],
          ],
        };
      }
    } else if (aff.weaponSubtype) {
      const label = WEAPON_SUBTYPE_PLURAL[aff.weaponSubtype] ?? aff.weaponSubtype;
      bank[aff.id] = {
        suffixes: [
          [`del Aprendiz de ${label}`, `del Discípulo de ${label}`],
          [`del Maestro de ${label}`, `del Veterano de ${label}`],
          [`del Gran Maestro de ${label}`, `del Virtuoso de ${label}`],
        ],
      };
    }
  }
  return bank;
})();

/** Orden estable de familias (índice en `AFFIX_POOL`) para repartir prefijo/sufijo determinístico. */
const AFFIX_ORDER: ReadonlyMap<string, number> = new Map(
  AFFIX_POOL.map((aff, i) => [affixRolledKey(aff), i])
);

// ─────────────────────────────────────────────────────────────────────────────
// Concordancia de género/número (español). Sufijos "de X" son invariantes; sólo
// los prefijos adjetivos concuerdan.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Concuerda un adjetivo dado en MASCULINO SINGULAR al género/número del item.
 * - Femenino: `-o` final → `-a` (Sanguinario→Sanguinaria; Letal/Feroz no terminan en `o` → invariantes).
 * - Plural: vocal final → `+s`; `-z` final → `-ces` (Feroz→Feroces); resto consonante → `+es`.
 */
export function agreeAdjective(base: string, gender: 'm' | 'f', plural = false): string {
  let w = base;
  if (gender === 'f' && /o$/.test(w)) w = `${w.slice(0, -1)}a`;
  if (plural) {
    if (/z$/i.test(w)) w = `${w.slice(0, -1)}ces`;
    else if (/[aeiouáéíóú]$/i.test(w)) w = `${w}s`;
    else w = `${w}es`;
  }
  return w;
}

/**
 * Infiere la gramática del nombre base a partir del sustantivo cabecera (primera
 * palabra). Cubre las bases actuales; se puede overridear con
 * `ItemDefinition.grammar` cuando la heurística falle.
 */
export function inferGrammar(name: string): ItemGrammar {
  const head = name.trim().split(/\s+/)[0] ?? '';
  const lower = head.toLowerCase();
  const plural = /s$/.test(lower); // Botas, Puños, Nudilleras...
  // Femenino si termina en 'a' (singular) o 'as' (plural). El resto → masculino.
  const gender: 'm' | 'f' = /as?$/.test(lower) ? 'f' : 'm';
  return { gender, plural };
}

// ─────────────────────────────────────────────────────────────────────────────
// Selección determinística (hash de los rolls) — variedad sin persistir ni RNG.
// ─────────────────────────────────────────────────────────────────────────────
function canonicalAffixes(rolled: RolledAffixes): string {
  return Object.keys(rolled)
    .sort()
    .map((k) => `${k}:${rolled[k].map((c) => `${c.v}@${c.t}`).join(',')}`)
    .join(';');
}

/** FNV-1a 32-bit (determinístico, sin Math.random — que está prohibido en este entorno). */
function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function pickWord(options: readonly string[] | undefined, seed: number): string {
  if (!options || options.length === 0) return '';
  return options[seed % options.length];
}

/** Tier (1..3) representativo de una familia = el más alto de sus componentes. */
function maxTier(components: ReadonlyArray<{ t: number }>): 1 | 2 | 3 {
  let t = 1;
  for (const c of components) if (c.t > t) t = c.t;
  return Math.min(3, Math.max(1, t)) as 1 | 2 | 3;
}

interface FamilyWords {
  prefix?: string;
  suffix?: string;
}

function wordsFor(key: string, tier: 1 | 2 | 3, seed: number): FamilyWords {
  const naming = AFFIX_NAMING[key];
  if (!naming) return {};
  return {
    prefix: naming.prefixes ? pickWord(naming.prefixes[tier - 1], seed) || undefined : undefined,
    suffix: naming.suffixes ? pickWord(naming.suffixes[tier - 1], seed) || undefined : undefined,
  };
}

/**
 * Construye el nombre derivado: `[prefijo concordado] base [sufijo]`. Determinístico
 * dado `rolled`. Con `rolled` vacío devuelve `baseName` tal cual.
 */
export function buildAffixDerivedName(
  baseName: string,
  rolled: RolledAffixes,
  grammar: ItemGrammar
): string {
  const keys = Object.keys(rolled);
  if (keys.length === 0) return baseName;

  const seed = hashString(canonicalAffixes(rolled));

  // Familias presentes, ordenadas por TIER DESC (el mejor roll define el adjetivo
  // destacado) y, a igual tier, por índice fijo en AFFIX_POOL (determinístico).
  const families = keys
    .map((k) => ({ key: k, tier: maxTier(rolled[k]) }))
    .sort((a, b) => b.tier - a.tier || (AFFIX_ORDER.get(a.key) ?? 999) - (AFFIX_ORDER.get(b.key) ?? 999));

  let prefixWord = '';
  let suffixWord = '';

  if (families.length === 1) {
    const f = families[0];
    const w = wordsFor(f.key, f.tier, seed);
    if (w.prefix) prefixWord = w.prefix;
    else if (w.suffix) suffixWord = w.suffix;
  } else {
    // 2 familias: la 1ª (más "core") tiende a prefijo, la 2ª a sufijo. Se varía el
    // seed por familia para que dos familias iguales no elijan el mismo índice.
    const a = families[0];
    const b = families[1];
    const aw = wordsFor(a.key, a.tier, seed);
    const bw = wordsFor(b.key, b.tier, (seed ^ 0x9e3779b9) >>> 0);

    if (aw.prefix && bw.suffix) {
      prefixWord = aw.prefix;
      suffixWord = bw.suffix;
    } else if (bw.prefix && aw.suffix) {
      prefixWord = bw.prefix;
      suffixWord = aw.suffix;
    } else {
      // Ambas suffix-only (o degradadas): un solo sufijo (invariante del banco:
      // una familia con prefijo siempre trae sufijo, así este caso es raro).
      suffixWord = aw.suffix || bw.suffix || '';
      prefixWord = aw.prefix || bw.prefix || '';
    }
  }

  // Orden español: sustantivo + adjetivo + "de X" (ej. "Espada larga Sanguinaria del Espectro").
  const parts: string[] = [baseName];
  if (prefixWord) parts.push(agreeAdjective(prefixWord, grammar.gender, grammar.plural));
  if (suffixWord) parts.push(suffixWord);
  return parts.join(' ');
}

/**
 * Verifica que TODA familia del `AFFIX_POOL` tenga entry en `AFFIX_NAMING` con
 * prefijos y/o sufijos completos (3 tiers, cada uno con ≥1 opción), y que el
 * invariante "prefijo ⟹ sufijo" se cumpla. Lanza con la lista de problemas.
 * Se corre en el harness de verificación (ver docs/ITEM_NAMING.md §Verificación).
 */
export function assertNamingCoverage(): void {
  const problems: string[] = [];
  const seen = new Set<string>();
  const tiersOk = (tw: TierWords | undefined): boolean =>
    !!tw && tw.length === 3 && tw.every((opts) => Array.isArray(opts) && opts.length > 0);

  for (const aff of AFFIX_POOL) {
    const key = affixRolledKey(aff);
    if (seen.has(key)) continue;
    seen.add(key);
    const naming = AFFIX_NAMING[key];
    if (!naming) {
      problems.push(`${key}: sin entry en AFFIX_NAMING`);
      continue;
    }
    const hasPrefix = naming.prefixes !== undefined;
    const hasSuffix = naming.suffixes !== undefined;
    if (!hasPrefix && !hasSuffix) {
      problems.push(`${key}: ni prefijos ni sufijos`);
      continue;
    }
    if (hasPrefix && !tiersOk(naming.prefixes)) problems.push(`${key}: prefijos incompletos (faltan tiers/opciones)`);
    if (hasSuffix && !tiersOk(naming.suffixes)) problems.push(`${key}: sufijos incompletos (faltan tiers/opciones)`);
    if (hasPrefix && !hasSuffix) problems.push(`${key}: tiene prefijos pero no sufijos (invariante prefijo⟹sufijo)`);
  }

  if (problems.length > 0) {
    throw new Error(`AFFIX_NAMING incompleto:\n- ${problems.join('\n- ')}`);
  }
}
